import { Button, Form, Input, Select, Spin, Tag, message } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import {
  CATCH_BOARD_BUYER_EMAIL_MAX_LENGTH,
  CATCH_BOARD_BUYER_NAME_MAX_LENGTH,
  CATCH_BOARD_BUYER_PHONE_MAX_LENGTH,
  CATCH_BOARD_BUYER_ROLE_KEY,
  CATCH_BOARD_BUYER_ROLE_MAX_LENGTH,
  CATCH_BOARD_BUYER_ROLE_META,
  CATCH_BOARD_MAX_BUYERS_PER_ROLE,
} from './catchBoardConstants'
import {
  addCatchBoardBuyer,
  emptyCatchBoardExternalBuyerForm,
  formatCatchBoardBuyerLabel,
  getCatchBoardBuyerKey,
  normalizeCatchBoardBuyers,
  removeCatchBoardBuyer,
  spartacusUserToCatchBoardBuyer,
} from 'src/utils/catchBoardBuyerHelpers'
import { loadCatchBoardVenueUsersForDeal } from 'src/utils/catchBoardVenueUsers'
import styled from 'styled-components/macro'

const { Option } = Select

function BuyerRolePanel({
  roleKey,
  buyers,
  venueUsers,
  venueUsersLoading,
  hasLinkedVenues,
  disabled,
  onChange,
}) {
  const meta = CATCH_BOARD_BUYER_ROLE_META[roleKey]
  const [selectedUserKey, setSelectedUserKey] = useState('')
  const [showExternal, setShowExternal] = useState(false)
  const [external, setExternal] = useState(emptyCatchBoardExternalBuyerForm)

  const atLimit = buyers.length >= CATCH_BOARD_MAX_BUYERS_PER_ROLE
  const existingKeys = useMemo(
    () => new Set(buyers.map(getCatchBoardBuyerKey)),
    [buyers],
  )

  const availableUsers = useMemo(
    () =>
      venueUsers.filter(user => {
        const buyer = spartacusUserToCatchBoardBuyer(user)
        return !existingKeys.has(getCatchBoardBuyerKey(buyer))
      }),
    [venueUsers, existingKeys],
  )

  const handleAddVenueUser = () => {
    if (!selectedUserKey) {
      message.info('Pick a venue user first')
      return
    }
    const user = venueUsers.find(row => {
      const buyer = spartacusUserToCatchBoardBuyer(row)
      return getCatchBoardBuyerKey(buyer) === selectedUserKey
    })
    if (!user) {
      return
    }

    const result = addCatchBoardBuyer(
      buyers,
      spartacusUserToCatchBoardBuyer(user),
    )
    if (result.error) {
      message.warning(result.error)
      return
    }
    onChange(result.buyers)
    setSelectedUserKey('')
  }

  const handleAddExternal = () => {
    const result = addCatchBoardBuyer(buyers, external)
    if (result.error) {
      message.warning(result.error)
      return
    }
    onChange(result.buyers)
    setExternal(emptyCatchBoardExternalBuyerForm())
    setShowExternal(false)
  }

  const handleRemove = buyerKey => {
    onChange(removeCatchBoardBuyer(buyers, buyerKey))
  }

  return (
    <RolePanel data-testid={`catch-board-buyers-${roleKey}`}>
      <RoleTitle>{meta.label}</RoleTitle>
      <RoleHint>{meta.hint}</RoleHint>

      {buyers.length === 0 ? (
        <EmptyBuyers data-testid={`catch-board-buyers-empty-${roleKey}`}>
          None added yet.
        </EmptyBuyers>
      ) : (
        <BuyerList>
          {buyers.map(buyer => {
            const key = getCatchBoardBuyerKey(buyer)
            return (
              <BuyerRow
                key={key}
                data-testid={`catch-board-buyer-${roleKey}-${key}`}
              >
                <BuyerMain>
                  <BuyerName title={formatCatchBoardBuyerLabel(buyer)}>
                    {formatCatchBoardBuyerLabel(buyer)}
                  </BuyerName>
                  {buyer.accountId ? (
                    <Tag color='blue'>Venue user</Tag>
                  ) : (
                    <Tag>External</Tag>
                  )}
                </BuyerMain>
                <Button
                  size='small'
                  type='danger'
                  ghost
                  disabled={disabled}
                  onClick={() => handleRemove(key)}
                  data-testid={`catch-board-buyer-remove-${roleKey}-${key}`}
                >
                  Remove
                </Button>
              </BuyerRow>
            )
          })}
        </BuyerList>
      )}

      <AddRow>
        {hasLinkedVenues ? (
          <>
            <Select
              style={{ flex: 1, minWidth: 180 }}
              dropdownMatchSelectWidth={false}
              dropdownStyle={{ minWidth: 220 }}
              value={selectedUserKey || undefined}
              placeholder={
                venueUsersLoading
                  ? 'Loading venue users…'
                  : availableUsers.length
                    ? 'Pick from venue users'
                    : 'No more venue users to add'
              }
              disabled={
                disabled ||
                atLimit ||
                venueUsersLoading ||
                availableUsers.length === 0
              }
              onChange={value => setSelectedUserKey(value || '')}
              data-testid={`catch-board-buyer-user-select-${roleKey}`}
            >
              {availableUsers.map(user => {
                const buyer = spartacusUserToCatchBoardBuyer(user)
                const key = getCatchBoardBuyerKey(buyer)
                const label = [
                  formatCatchBoardBuyerLabel(buyer),
                  user.venueName ? `(${user.venueName})` : '',
                ]
                  .filter(Boolean)
                  .join(' ')
                return (
                  <Option key={key} value={key}>
                    {label}
                  </Option>
                )
              })}
            </Select>
            <Button
              size='small'
              disabled={disabled || atLimit || !selectedUserKey}
              onClick={handleAddVenueUser}
              data-testid={`catch-board-buyer-add-user-${roleKey}`}
            >
              Add user
            </Button>
          </>
        ) : (
          <NoVenueHint>
            Link a venue above to pick buyers from venue users.
          </NoVenueHint>
        )}
      </AddRow>

      {!showExternal ? (
        <Button
          size='small'
          type='dashed'
          disabled={disabled || atLimit}
          onClick={() => setShowExternal(true)}
          data-testid={`catch-board-buyer-show-external-${roleKey}`}
        >
          Add external contact
        </Button>
      ) : (
        <ExternalBox data-testid={`catch-board-buyer-external-${roleKey}`}>
          <Form.Item label='Name' style={{ marginBottom: 8 }}>
            <Input
              value={external.name}
              maxLength={CATCH_BOARD_BUYER_NAME_MAX_LENGTH}
              disabled={disabled}
              onChange={event =>
                setExternal(prev => ({ ...prev, name: event.target.value }))
              }
              data-testid={`catch-board-buyer-external-name-${roleKey}`}
            />
          </Form.Item>
          <Form.Item label='Email' style={{ marginBottom: 8 }}>
            <Input
              value={external.email}
              maxLength={CATCH_BOARD_BUYER_EMAIL_MAX_LENGTH}
              disabled={disabled}
              onChange={event =>
                setExternal(prev => ({ ...prev, email: event.target.value }))
              }
              data-testid={`catch-board-buyer-external-email-${roleKey}`}
            />
          </Form.Item>
          <ExternalRow>
            <Form.Item
              label='Role'
              style={{ marginBottom: 8, flex: 1 }}
            >
              <Input
                value={external.role}
                maxLength={CATCH_BOARD_BUYER_ROLE_MAX_LENGTH}
                disabled={disabled}
                onChange={event =>
                  setExternal(prev => ({ ...prev, role: event.target.value }))
                }
                data-testid={`catch-board-buyer-external-role-${roleKey}`}
              />
            </Form.Item>
            <Form.Item
              label='Phone'
              style={{ marginBottom: 8, flex: 1 }}
            >
              <Input
                value={external.phone}
                maxLength={CATCH_BOARD_BUYER_PHONE_MAX_LENGTH}
                disabled={disabled}
                onChange={event =>
                  setExternal(prev => ({ ...prev, phone: event.target.value }))
                }
                data-testid={`catch-board-buyer-external-phone-${roleKey}`}
              />
            </Form.Item>
          </ExternalRow>
          <ExternalActions>
            <Button
              size='small'
              type='primary'
              disabled={disabled || atLimit}
              onClick={handleAddExternal}
              data-testid={`catch-board-buyer-add-external-${roleKey}`}
            >
              Add contact
            </Button>
            <Button
              size='small'
              disabled={disabled}
              onClick={() => {
                setShowExternal(false)
                setExternal(emptyCatchBoardExternalBuyerForm())
              }}
            >
              Cancel
            </Button>
          </ExternalActions>
        </ExternalBox>
      )}
    </RolePanel>
  )
}

/**
 * Economic + user buyers — pick from linked venue users or add external.
 */
const CatchBoardDealBuyersSection = ({
  form,
  onFormChange,
  disabled = false,
}) => {
  const linkedVenues = form?.linkedVenues || []
  const primaryVenueId = form?.primaryVenueId || null
  const [venueUsers, setVenueUsers] = useState([])
  const [venueUsersLoading, setVenueUsersLoading] = useState(false)
  const [venueUsersError, setVenueUsersError] = useState(null)

  const linkedVenueKey = linkedVenues.map(row => row.venueId).join(',')

  useEffect(() => {
    const venues = linkedVenueKey
      ? linkedVenueKey.split(',').map(venueId => ({ venueId, name: '' }))
      : []

    if (!venues.length) {
      setVenueUsers([])
      setVenueUsersLoading(false)
      setVenueUsersError(null)
      return undefined
    }

    let cancelled = false

    async function loadUsers() {
      setVenueUsersLoading(true)
      setVenueUsersError(null)
      try {
        const { users, errors } = await loadCatchBoardVenueUsersForDeal(
          venues,
          { primaryVenueId },
        )
        if (cancelled) {
          return
        }
        setVenueUsers(users)
        setVenueUsersError(errors.length ? errors.join(' · ') : null)
      } catch (error) {
        if (!cancelled) {
          setVenueUsers([])
          setVenueUsersError(error.message || 'Failed to load venue users')
        }
      } finally {
        if (!cancelled) {
          setVenueUsersLoading(false)
        }
      }
    }

    loadUsers()

    return () => {
      cancelled = true
    }
  }, [linkedVenueKey, primaryVenueId])

  const patchBuyers = (roleKey, buyers) => {
    onFormChange?.({
      ...form,
      [roleKey]: normalizeCatchBoardBuyers(buyers),
    })
  }

  return (
    <Section data-testid='catch-board-deal-buyers'>
      <SectionTitle>Buyers</SectionTitle>
      <SectionHint>
        Track economic and user buyers. Pick from linked venue users or add an
        external contact.
      </SectionHint>

      {venueUsersLoading ? (
        <LoadingRow>
          <Spin size='small' />
          <span>Loading venue users…</span>
        </LoadingRow>
      ) : null}

      {venueUsersError ? (
        <UsersError data-testid='catch-board-buyers-users-error'>
          {venueUsersError}
        </UsersError>
      ) : null}

      <BuyerRolePanel
        roleKey={CATCH_BOARD_BUYER_ROLE_KEY.ECONOMIC}
        buyers={normalizeCatchBoardBuyers(form.economicBuyers)}
        venueUsers={venueUsers}
        venueUsersLoading={venueUsersLoading}
        hasLinkedVenues={linkedVenues.length > 0}
        disabled={disabled}
        onChange={buyers =>
          patchBuyers(CATCH_BOARD_BUYER_ROLE_KEY.ECONOMIC, buyers)
        }
      />

      <BuyerRolePanel
        roleKey={CATCH_BOARD_BUYER_ROLE_KEY.USER}
        buyers={normalizeCatchBoardBuyers(form.userBuyers)}
        venueUsers={venueUsers}
        venueUsersLoading={venueUsersLoading}
        hasLinkedVenues={linkedVenues.length > 0}
        disabled={disabled}
        onChange={buyers =>
          patchBuyers(CATCH_BOARD_BUYER_ROLE_KEY.USER, buyers)
        }
      />
    </Section>
  )
}

const Section = styled.div`
  margin-top: 4px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);
`

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const SectionHint = styled.p`
  margin: 4px 0 12px;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const UsersError = styled.div`
  margin-bottom: 8px;
  font-size: 12px;
  color: #a94442;
`

const RolePanel = styled.div`
  margin-bottom: 16px;
  padding: 10px 12px;
  background: #f7f9f8;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
`

const RoleTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
`

const RoleHint = styled.p`
  margin: 2px 0 10px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const EmptyBuyers = styled.div`
  margin-bottom: 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
`

const BuyerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`

const BuyerRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 6px;
`

const BuyerMain = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const BuyerName = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: 600;
`

const AddRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const NoVenueHint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const ExternalBox = styled.div`
  margin-top: 4px;
  padding: 10px;
  background: #fff;
  border: 1px dashed rgba(0, 0, 0, 0.12);
  border-radius: 6px;
`

const ExternalRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0 12px;
`

const ExternalActions = styled.div`
  display: flex;
  gap: 8px;
`

export default CatchBoardDealBuyersSection
